import * as WebBrowser from 'expo-web-browser';
import { useAnalytics } from '@/components/analytics-provider';
import * as React from 'react';
import { ActivityIndicator, Image, Platform, View } from 'react-native';

import { Button } from '@sixam/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@sixam/ui/card';
import { Text } from '@sixam/ui/text';
import { getApiUrl } from '@/lib/api-url';
import { authedFetch } from '@/lib/authed-fetch';
import type { InstagramIntegrationStatus } from '@/lib/integrations/types';

WebBrowser.maybeCompleteAuthSession();

type InstagramConnectionProps = Readonly<{
  /** Owner query params for the API (e.g. `tenant_id=…`). Empty string = current user. */
  ownerQuery?: string;
  /** Tenant-scoped page the OAuth callback should return to (e.g. `/admin/6am.bkk/integrations/instagram`). */
  returnPath?: string;
  /** Label shown under the heading (e.g. tenant name or "Your account"). */
  scopeLabel?: string;
  /** Copy describing which account should be linked. */
  description: string;
  /** Banner state from the OAuth redirect. */
  connected?: string;
  error?: string;
}>;

function withOwner(path: string, ownerQuery?: string): string {
  if (!ownerQuery) {
    return path;
  }
  return path.includes('?') ? `${path}&${ownerQuery}` : `${path}?${ownerQuery}`;
}

async function fetchInstagramStatus(ownerQuery?: string): Promise<InstagramIntegrationStatus> {
  const response = await authedFetch(withOwner('/api/integrations/instagram', ownerQuery));

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? 'Failed to load Instagram status');
  }

  return (await response.json()) as InstagramIntegrationStatus;
}

async function disconnectInstagram(ownerQuery?: string): Promise<void> {
  const response = await authedFetch(withOwner('/api/integrations/instagram', ownerQuery), {
    method: 'DELETE',
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? 'Failed to disconnect Instagram');
  }
}

function getBannerMessage(connected?: string, error?: string): string | null {
  if (connected === '1') {
    return 'Instagram connected successfully.';
  }
  if (error) {
    return `Instagram connection failed: ${error}`;
  }
  return null;
}

function InstagramProfile({
  status,
  isDisconnecting,
  onDisconnect,
}: Readonly<{
  status: InstagramIntegrationStatus;
  isDisconnecting: boolean;
  onDisconnect: () => void;
}>) {
  return (
    <View className="gap-4">
      <View className="flex-row items-center gap-4">
        {status.profilePictureUrl ? (
          <Image source={{ uri: status.profilePictureUrl }} className="size-16 rounded-full" />
        ) : null}
        <View className="flex-1 gap-1">
          <Text className="text-lg font-semibold">
            @{status.username}
            {status.displayName ? ` · ${status.displayName}` : ''}
          </Text>
          {status.biography ? <Text variant="muted">{status.biography}</Text> : null}
        </View>
      </View>

      <View className="gap-1">
        {status.website ? <Text variant="small">Website: {status.website}</Text> : null}
        {status.followersCount != null ? (
          <Text variant="small">Followers: {status.followersCount.toLocaleString()}</Text>
        ) : null}
        {status.mediaCount != null ? (
          <Text variant="small">Posts: {status.mediaCount.toLocaleString()}</Text>
        ) : null}
        {status.connectedAt ? (
          <Text variant="small" className="text-muted-foreground">
            Connected {new Date(status.connectedAt).toLocaleString()}
          </Text>
        ) : null}
      </View>

      <Button variant="destructive" onPress={onDisconnect} disabled={isDisconnecting}>
        <Text>{isDisconnecting ? 'Disconnecting…' : 'Disconnect Instagram'}</Text>
      </Button>
    </View>
  );
}

export function InstagramConnection({
  ownerQuery,
  returnPath,
  scopeLabel,
  description,
  connected,
  error,
}: InstagramConnectionProps) {
  const posthog = useAnalytics();
  const [status, setStatus] = React.useState<InstagramIntegrationStatus | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isDisconnecting, setIsDisconnecting] = React.useState(false);
  const [actionError, setActionError] = React.useState<string | null>(null);

  const bannerMessage = getBannerMessage(connected, error);

  React.useEffect(() => {
    let active = true;

    async function load() {
      if (active) {
        setIsLoading(true);
        setActionError(null);
      }

      try {
        const nextStatus = await fetchInstagramStatus(ownerQuery);
        if (active) {
          setStatus(nextStatus);
        }
      } catch (loadError) {
        if (active) {
          setActionError(loadError instanceof Error ? loadError.message : 'Failed to load status');
          setStatus(null);
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    }

    void load();

    return () => {
      active = false;
    };
  }, [ownerQuery, connected]);

  async function handleConnect() {
    setActionError(null);
    const ownerScoped = withOwner(getApiUrl('/api/integrations/instagram/authorize'), ownerQuery);
    let authorizeUrl = ownerScoped;
    if (returnPath) {
      const separator = ownerScoped.includes('?') ? '&' : '?';
      authorizeUrl = `${ownerScoped}${separator}return_path=${encodeURIComponent(returnPath)}`;
    }

    if (Platform.OS === 'web') {
      window.location.assign(authorizeUrl);
      return;
    }

    const result = await WebBrowser.openAuthSessionAsync(authorizeUrl, getApiUrl('/'));
    if (result.type === 'success') {
      try {
        setStatus(await fetchInstagramStatus(ownerQuery));
      } catch (loadError) {
        setActionError(loadError instanceof Error ? loadError.message : 'Failed to load status');
      }
    }
  }

  async function handleDisconnect() {
    setIsDisconnecting(true);
    setActionError(null);

    try {
      await disconnectInstagram(ownerQuery);
      posthog.capture('instagram_disconnected', {
        scope: ownerQuery ? 'club' : 'member',
      });
      setStatus(await fetchInstagramStatus(ownerQuery));
    } catch (disconnectError) {
      setActionError(
        disconnectError instanceof Error ? disconnectError.message : 'Failed to disconnect'
      );
    } finally {
      setIsDisconnecting(false);
    }
  }

  let body: React.ReactNode;
  if (isLoading) {
    body = <ActivityIndicator />;
  } else if (status?.connected) {
    body = (
      <InstagramProfile
        status={status}
        isDisconnecting={isDisconnecting}
        onDisconnect={() => void handleDisconnect()}
      />
    );
  } else {
    body = (
      <View className="gap-3">
        <Text variant="muted">
          No Instagram account is connected yet. You will be redirected to Meta to authorize read
          access to profile info.
        </Text>
        <Button onPress={() => void handleConnect()}>
          <Text>Connect Instagram account</Text>
        </Button>
      </View>
    );
  }

  return (
    <View className="gap-4">
      {bannerMessage ? (
        <Card>
          <CardContent className="pt-6">
            <Text className={error ? 'text-destructive' : undefined}>{bannerMessage}</Text>
          </CardContent>
        </Card>
      ) : null}

      {actionError ? (
        <Card>
          <CardContent className="pt-6">
            <Text className="text-destructive">{actionError}</Text>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Account connection</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent className="gap-4">
          {scopeLabel ? <Text variant="muted">{scopeLabel}</Text> : null}
          {body}
        </CardContent>
      </Card>
    </View>
  );
}
